import React, { useEffect, useState } from 'react';
import { isDismissed, dismiss, TIPS_RESTORED_EVENT } from '../../utils/dismissals';
import './dismissible.css';

/**
 * Thin wrapper around a tip/callout block that adds a persistent ✕ dismiss
 * control. It does NOT own any visual styling of the callout itself — the
 * block being wrapped keeps whatever background/border/heading it already
 * has; this component only adds `position: relative` and an absolutely
 * positioned close button on top of it.
 *
 * `title` is accepted for API completeness / future use (e.g. richer
 * aria-labelling) but is not rendered — the wrapped children already carry
 * their own heading text, and duplicating it here would show it twice.
 */
function Dismissible({ id, title, children, userSub }) {
  const [dismissed, setDismissedState] = useState(() => isDismissed(userSub, id));

  useEffect(() => {
    // Re-check on mount in case userSub/id weren't ready on first render.
    setDismissedState(isDismissed(userSub, id));
  }, [userSub, id]);

  useEffect(() => {
    const onRestored = () => setDismissedState(isDismissed(userSub, id));
    window.addEventListener(TIPS_RESTORED_EVENT, onRestored);
    return () => window.removeEventListener(TIPS_RESTORED_EVENT, onRestored);
  }, [userSub, id]);

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    dismiss(userSub, id);
    setDismissedState(true);
  };

  return (
    <div className="dismissible">
      <button
        type="button"
        className="dismissible__close"
        aria-label="Dismiss tip"
        title={title}
        onClick={handleDismiss}
      >
        ✕
      </button>
      {children}
    </div>
  );
}

export default Dismissible;
